/**
 * useHighlights Hook
 *
 * Custom hook for managing article highlights including creation,
 * retrieval, deletion, and detachment detection.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  createHighlight as createHighlightAPI,
  getArticleHighlights,
  deleteHighlight as deleteHighlightAPI,
  deleteAllHighlights as deleteAllHighlightsAPI,
  checkHighlightDetachment,
  reattachHighlights as reattachHighlightsAPI,
} from '../services/highlights';
import { findTextPosition } from '../utils/highlightTextMatching';

/**
 * Calculate SHA-256 hash for summary version tracking
 * @param {string} text - Text to hash
 * @returns {Promise<string>} Hex string of hash
 */
async function calculateSummaryHash(text) {
  const input = text || '';
  // crypto.subtle requires a secure context (HTTPS/localhost).
  // Fall back to a simple hash for HTTP (e.g., LAN testing from a phone).
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback: simple hash — not cryptographic, but fine for version tracking.
  // Run multiple rounds with different seeds to fill 64 hex chars (SHA-256 length).
  const parts = [];
  for (let seed = 0; parts.length < 8; seed++) {
    let h = 5381;
    h = ((h << 5) + h + seed * 7919) >>> 0;
    for (let i = 0; i < input.length; i++) {
      h = ((h << 5) + h + input.charCodeAt(i)) >>> 0;
    }
    h = ((h << 5) + h + seed) >>> 0;
    parts.push(h.toString(16).padStart(8, '0'));
  }
  return parts.join('');
}

/**
 * Extract context around selected text
 * @param {string} fullText - Full text content
 * @param {number} start - Start position
 * @param {number} end - End position
 * @param {number} contextLength - Length of context to extract (default: 50)
 * @returns {Object} Object with prefix and suffix context
 */
function extractContext(fullText, start, end, contextLength = 50) {
  const prefix = fullText.substring(Math.max(0, start - contextLength), start);
  const suffix = fullText.substring(end, Math.min(fullText.length, end + contextLength));

  return { prefix, suffix };
}

export function useHighlights(articleId, summaryText) {
  const [highlights, setHighlights] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [detachmentInfo, setDetachmentInfo] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Fetch highlights for the current article
   */
  const fetchHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getArticleHighlights(articleId, false);
      setHighlights(response.highlights || []);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError(err.message);
      setHighlights([]);
    } finally {
      setIsLoading(false);
    }
  }, [articleId]);

  /**
   * Check for detached highlights
   */
  const checkDetachment = useCallback(async () => {
    if (!articleId) return;

    try {
      const response = await checkHighlightDetachment(articleId);
      setDetachmentInfo(response);
    } catch (err) {
      console.error('Error checking detachment:', err);
    }
  }, [articleId]);

  /**
   * Create a new highlight
   * @param {string} selectedText - The exact text being highlighted
   * @param {Object} selectionRange - Selection range with start/end positions
   * @returns {Promise<Object>} Created highlight
   */
  const createHighlight = useCallback(
    async (selectedText, selectionRange = null, renderedText = null) => {
      if (!articleId || !summaryText || !selectedText) {
        throw new Error('Missing required data for highlight creation');
      }

      // Use rendered text if provided, otherwise fall back to summary text
      const textForPositioning = renderedText || summaryText;

      try {
        setIsCreating(true);
        setError(null);

        // Calculate summary hash for version tracking
        const summaryHash = await calculateSummaryHash(summaryText);

        // Find text position if not provided
        let position = selectionRange;
        if (!position) {
          // console.log('[createHighlight] selectedText:', selectedText.substring(0, 100));
          // console.log('[createHighlight] selectedText has bold?', selectedText.includes('**'));
          // console.log('[createHighlight] textForPositioning (first 200):', textForPositioning.substring(0, 200));

          position = findTextPosition(textForPositioning, selectedText);
          if (!position) {
            // console.log('[createHighlight] ❌ Could not find position');
            // console.log('[createHighlight] Normalized selected:', normalizeText(selectedText).substring(0, 100));
            // console.log('[createHighlight] Normalized rendered:', normalizeText(textForPositioning).substring(0, 100));
            throw new Error('Could not find selected text in rendered content');
          }

          // console.log('[createHighlight] Found position:', position);
          // console.log('[createHighlight] Text at position:', textForPositioning.substring(position.start, Math.min(position.start + 100, textForPositioning.length)));
        }

        // Extract context around selection
        const context = extractContext(textForPositioning, position.start, position.end);

        // Build W3C Web Annotation structure with multi-selector strategy
        const annotation = {
          '@context': 'http://www.w3.org/ns/anno.jsonld',
          type: 'Annotation',
          body: {
            type: 'TextualBody',
            value: selectedText,
            format: 'text/plain',
          },
          target: {
            selector: [
              // RangeSelector (fast, fragile)
              {
                type: 'RangeSelector',
                startSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
                endSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
              },
              // TextPositionSelector (fast, moderately fragile)
              {
                type: 'TextPositionSelector',
                start: position.start,
                end: position.end,
              },
              // TextQuoteSelector (slow, robust)
              {
                type: 'TextQuoteSelector',
                exact: selectedText,
                prefix: context.prefix,
                suffix: context.suffix,
              },
            ],
          },
        };

        // Create highlight via API
        const highlightData = {
          annotation,
          exact_text: selectedText,
          summary_version: summaryHash,
        };

        const newHighlight = await createHighlightAPI(articleId, highlightData);

        // Add to local state
        setHighlights((prev) => [...prev, newHighlight]);

        return newHighlight;
      } catch (err) {
        console.error('Error creating highlight:', err);
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [articleId, summaryText]
  );

  /**
   * Delete a highlight
   * @param {number} highlightId - Highlight ID to delete
   */
  const deleteHighlight = useCallback(async (highlightId) => {
    try {
      setError(null);
      await deleteHighlightAPI(highlightId);

      // Remove from local state
      setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
    } catch (err) {
      console.error('Error deleting highlight:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete all highlights for the current article
   */
  const deleteAllHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setError(null);
      await deleteAllHighlightsAPI(articleId);

      // Clear local state
      setHighlights([]);
    } catch (err) {
      console.error('Error deleting all highlights:', err);
      setError(err.message);
      throw err;
    }
  }, [articleId]);

  /**
   * Re-attach detached highlights
   * @param {number} similarityThreshold - Minimum similarity threshold (0.0-1.0)
   * @returns {Promise<Object>} Re-attachment results
   */
  const reattachHighlights = useCallback(
    async (similarityThreshold = 0.8) => {
      if (!articleId) return;

      try {
        setError(null);
        const response = await reattachHighlightsAPI(articleId, similarityThreshold);

        // Refresh highlights after re-attachment
        await fetchHighlights();
        await checkDetachment();

        return response;
      } catch (err) {
        console.error('Error re-attaching highlights:', err);
        setError(err.message);
        throw err;
      }
    },
    [articleId, fetchHighlights, checkDetachment]
  );

  // Fetch highlights when article changes
  useEffect(() => {
    if (articleId) {
      fetchHighlights();
      checkDetachment();
    }
  }, [articleId, fetchHighlights, checkDetachment]);

  return {
    highlights,
    isLoading,
    isCreating,
    error,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    fetchHighlights,
    reattachHighlights,
    checkDetachment,
  };
}
